import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { changeAppLanguage, normalizeLanguage } from '../lib/language';

// Offset del tope para el ancla flotante de las pantallas des-logueadas.
// Mismo patrón F4.2c de los toasts vs Dynamic Island (ui/sonner.jsx
// TOAST_TOP_OFFSET; misma forma que ACTION_BAR_BOTTOM_CLASS y el header de
// App.js): en la app iOS el WebView corre DEBAJO de la barra de estado, y el
// `top-4` fijo dejaba el EN|ES detrás de los iconos de señal/wifi/batería.
// Incondicional a propósito: en web env() colapsa a 0px y el resultado es el
// 1rem (top-4) de siempre — cero cambio visible; en la app baja exactamente
// lo que mida la barra de estado/notch.
export const FLOATING_LANGUAGE_TOP_CLASS = 'top-[calc(1rem+env(safe-area-inset-top,0px))]';

// Ancla compartida del toggle EN|ES en las pantallas des-logueadas (login,
// signup, verify-email, forgot/reset password). Antes cada página duplicaba el
// mismo <div className="fixed top-4 right-4 z-50"> — extraído aquí para que el
// offset de safe-area viva UNA sola vez. Mismo lugar, mismo estilo; solo el
// tope pasa de top-4 al calc() de arriba.
export function FloatingLanguageSelector() {
  return (
    <div
      className={`fixed right-4 z-50 ${FLOATING_LANGUAGE_TOP_CLASS}`}
      data-testid="floating-language-selector"
    >
      <LanguageSelector />
    </div>
  );
}

export default function LanguageSelector({ variant = 'compact', alElegir }) {
  const { i18n, t } = useTranslation('common');
  const { user } = useAuth();

  const currentLang = normalizeLanguage(i18n.language);

  // Apply + persist through the single central mechanism (lib/language.js) —
  // this component only renders the toggle.
  // post-t7-2a (bloque F): `alElegir`, opcional, toma el toque en su lugar. La portada se lo pasa
  // al selector de su menu de tres rayas para que tambien navegue a la gemela de idioma (y ella
  // llama al mecanismo central). Sin la prop, exactamente lo de siempre.
  const setLang = alElegir || ((lang) => changeAppLanguage(lang, user));

  // pantallas-alumno (G): la variante de Ajustes es el control de dos botones del Panel (parte 2, "Idioma":
  // EN y ES, el elegido en blanco con letra negra; 36 en computadora y 44 en el celular), la misma forma que
  // `components/alumno/PestanasDeDosBotones.js` (variante idioma). Se escribe aqui y no con esa pieza porque la
  // pieza no avisa al tocar el ya elegido, y este selector siempre llama a `setLang` (lo de hoy). Mismos testids.
  if (variant === 'settings') {
    const boton = (lang) => (
      <button
        type="button"
        role="radio"
        aria-checked={currentLang === lang}
        onClick={() => setLang(lang)}
        className={`flex-1 basis-0 min-w-0 flex items-center justify-center px-3 min-h-[44px] md:min-h-[36px] rounded-lg text-[13px] md:text-[14px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${currentLang === lang ? 'bg-white text-black' : 'text-[#9A9A9A] hoverable:text-white'}`}
        data-testid={`lang-${lang}-btn`}
      >
        {lang.toUpperCase()}
      </button>
    );
    return (
      <div role="radiogroup" aria-label={t('language_selector.label')} data-testid="language-selector-full"
        className="inline-flex w-[132px] max-md:w-[156px] items-center gap-1 p-1 bg-[#111111] border border-[#222222] rounded-xl">
        {boton('en')}
        {boton('es')}
      </div>
    );
  }

  if (variant === 'mobile-menu' || variant === 'settings') {
    // Full-width row variant. In Settings the card title already labels this control,
    // so the secondary inline label is omitted and the toggle is centered.
    const isSettings = variant === 'settings';
    // post-t7-2c (bloque B, anexo B.6): el renglon del menu del telefono va sin relleno horizontal,
    // para que su rotulo arranque en el mismo borde que los enlaces del menu. Settings, intacto.
    return (
      <div className={isSettings ? 'flex flex-col items-center gap-3' : 'flex items-center justify-between py-2 rounded-lg'} data-testid="language-selector-full">
        {!isSettings && <span className="text-sm text-[#999]">{t('language_selector.label')}</span>}
        <div className="flex gap-1">
          <button
            onClick={() => setLang('en')}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${currentLang === 'en' ? 'bg-[#FF6B00] text-white' : 'bg-[#1a1a1a] text-[#999] hover:bg-[#2a2a2a]'}`}
            data-testid="lang-en-btn"
          >
            EN
          </button>
          <button
            onClick={() => setLang('es')}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${currentLang === 'es' ? 'bg-[#FF6B00] text-white' : 'bg-[#1a1a1a] text-[#999] hover:bg-[#2a2a2a]'}`}
            data-testid="lang-es-btn"
          >
            ES
          </button>
        </div>
      </div>
    );
  }

  // Compact inline variant for nav
  return (
    <div className="flex items-center gap-1 text-xs" data-testid="language-selector-compact">
      <button
        onClick={() => setLang('en')}
        className={`px-2 py-1 rounded transition-colors ${currentLang === 'en' ? 'text-white font-semibold' : 'text-[#888] hover:text-white'}`}
        data-testid="lang-en-btn-compact"
      >
        EN
      </button>
      <span className="text-[#444]">|</span>
      <button
        onClick={() => setLang('es')}
        className={`px-2 py-1 rounded transition-colors ${currentLang === 'es' ? 'text-white font-semibold' : 'text-[#888] hover:text-white'}`}
        data-testid="lang-es-btn-compact"
      >
        ES
      </button>
    </div>
  );
}
